import type { Board } from "./[boardId].page";
import Icon, {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, theme, Tooltip } from "antd";
import { PureComponent, useCallback, useEffect, useRef, useState } from "react";
import { UndoOutlined } from "@ant-design/icons";
import { useRouter } from "next/router";
import { SpaceItemType } from "pages/api/[orgId]/spaces/types";
import { useApplicationContext } from "src/hooks/useApplication";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { Flex } from "src/components/Flex";
import { SearchState } from "src/search/types";
import { ToasterMessageType, useToasterContext } from "src/hooks/useToaster";
import { ControlledQuestionInput as QuestionInput } from "src/search/components/inputs/question-input/ControlledQuestionInput";
import {
  DashboardV1State,
  DeepLinkType,
  SnippetV1State,
} from "pages/api/deep-links/types";
import { AddToSpacePicker } from "../space/AddToSpacePicker";
import { DashboardEditor } from "../dashboards/DashboardEditor";
import { TemplatePill } from "src/components/TemplatePill";
import { StarIcon } from "src/components/StarIcon";
import { FavoriteButton } from "src/components/FavoriteButton";
import { BoardItem } from "src/components/useLibrary";
import BoardFilters from "./BoardFilter";

export function BoardHeader({
  board,
  showBoardFilters = true,
}: {
  board: Board;
  showBoardFilters?: boolean;
}) {
  const router = useRouter();
  const [ready, setReady] = useState(false);

  // Simple effect to set ready state when router query is available
  useEffect(() => {
    if (!ready && router.isReady) {
      setReady(true);
    }
  }, [ready, router.isReady]);

  // Set default duration only if no date parameters exist

  return (
    <div
      style={{
        gap: 16,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        padding: "8px 16px 8px 8px",
      }}
    >
      {ready ? (
        <>
          <div
            style={{
              minWidth: 300,
              maxWidth: showBoardFilters ? "50%" : "100%",
              flex: "1",
            }}
          >
            <BoardTitleBar board={board} />
          </div>
          {showBoardFilters && (
            <div
              style={{
                maxWidth: "100%",
                borderRadius: 12,
                position: "relative",
                minWidth: "200px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <BoardFilters board={board} />
            </div>
          )}
        </>
      ) : (
        <Flex style={{ height: 54 }}>
          <LoadingOutlined />
        </Flex>
      )}
    </div>
  );
}

function BoardTitleBar({ board }: { board: Board }) {
  const app = useApplicationContext();
  const isTemplate =
    !board.link?.userId && !board.link?.orgId && !app.isSuperAdmin;
  //add pill if in dev mode
  const isDev = process.env.NODE_ENV === "development";

  return (
    <div style={{ maxWidth: "100%", minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: `${board.link?.description ? 2 : 14}px 8px 2px`,
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            fontSize: 24,
            fontWeight: 600,
            minWidth: 0,
            maxWidth: "100%",
            gap: "16px",
          }}
          className="trunc"
        >
          <span
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {board.link?.name || board.id}
          </span>
          <ButtonBar board={board} />
          {(isTemplate || isDev) && (
            <TemplatePill
              title={board.link?.name}
              description={board.link?.description}
            />
          )}
        </div>
      </div>
      {board.link?.description && (
        <div
          className="line-clamp-2"
          style={{
            paddingLeft: 8,
            maxWidth: "100%",
            fontSize: 14,
            color: "rgba(0, 0, 0, 0.45)",
            marginTop: 4,
            wordWrap: "break-word",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {board.link?.description}
        </div>
      )}
    </div>
  );
}

function ButtonBar({ board }: { board: Board }) {
  const app = useApplicationContext();
  const { token } = theme.useToken();
  const router = useRouter();
  const deep = useDeepLinks(DeepLinkType.BOARD);
  const toaster = useToasterContext();
  const [isEditingDetails, editDetails] = useState(false);
  const [showingSpacePicker, showAddToSpacePicker] = useState(false);

  const boardIsGlobal = !board.link?.orgId;
  const boardIsShared = !boardIsGlobal && !board.link?.userId;
  const boardIsPrivate = !boardIsGlobal && !boardIsShared;
  const scope = boardIsGlobal
    ? "global"
    : boardIsShared
    ? "workspace"
    : "private";

  const editorTags = (
    deep.links.data
      ?.map((board_) =>
        boardIsGlobal && !board_.orgId
          ? (board_.json as DashboardV1State | undefined)?.tags
          : boardIsShared && !board_.userId
          ? (board_.json as DashboardV1State | undefined)?.tags
          : boardIsPrivate && board_.userId
          ? (board_.json as DashboardV1State | undefined)?.tags
          : undefined
      )
      .filter(Boolean) || []
  ).flat() as string[];

  const working =
    deep.update.isLoading || deep.remove.isLoading || deep.duplicate.isLoading;

  return (
    <>
      {board.link && isEditingDetails && board.userCanEdit && (
        <DashboardEditor
          name={board.link.name}
          extra={{
            colorTheme: board.link.json.colorTheme,
            columnCount: board.link.json.columnCount,
            defaultDateRange: board.link.json.defaultDateRange,
            defaultFilters: board.link.json.defaultFilters,
            rowHeight: board.link.json.rowHeight,
            spacing: board.link.json.spacing,
            systemName: board.link.json.systemName,
            tags: board.link.json.tags,
          }}
          searchState={board.globalState}
          onSave={(data) => {
            if (!data || !board.link) throw new Error("expected data to save");
            return board.save({
              id: board.link.id,
              name: data.name || board.link.name,
              description: data.description,
              thumbnail: data.thumbnail,
              scope: data.scope,
              ...board.link?.json,
              colorTheme: data.colorTheme,
              columnCount: data.columnCount,
              defaultDateRange: data.defaultDateRange,
              defaultFilters: data.defaultFilters,
              rowHeight: data.rowHeight,
              spacing: data.spacing || 0,
              systemName: data.systemName,
              tags: data.tags,
            });
          }}
          setName={() => {}} // We aren't using the form in
          setDescription={() => {}} // the same way over here.
          setExtra={() => {}} // So these setters aren't needed.
          setScope={() => {}}
          description={board.link.description || ""}
          thumbnail={board.link.thumbnail}
          scope={scope}
          tags={editorTags}
          onClose={() => editDetails(false)}
        />
      )}
      {showingSpacePicker && board.link && (
        <AddToSpacePicker
          open
          target={
            document.querySelector("#primary-query-menu-button") as HTMLElement
          }
          onClose={() => showAddToSpacePicker(false)}
          item={{
            type: SpaceItemType.Board,
            name: board.link.name,
            description: board.link.description,
            config: { deep_link_id: board.link.id },
          }}
        >
          {null}
        </AddToSpacePicker>
      )}
      <FavoriteButton item={board.link as BoardItem} />
      {board.link && (
        <Dropdown
          trigger={["click"]}
          placement={"bottom"}
          arrow
          menu={{
            items: [
              {
                key: "configure",
                icon: <EditOutlined />,
                label: <span>Board Settings</span>,
                disabled: !board.userCanEdit || working,
                onClick() {
                  editDetails(true);
                },
              },
              {
                key: "space",
                icon: <Icon component={() => <PlusOutlined />} />,
                label: <span>Add Board to Space</span>,
                onClick() {
                  showAddToSpacePicker(true);
                },
              },
              { type: "divider" },
              {
                key: "duplicate",
                icon: <CopyOutlined />,
                label: <span>Duplicate</span>,
                disabled: working,
                onClick() {
                  if (!board.link) return;
                  deep.duplicate
                    .mutateAsync({ id: board.link.id })
                    .then((link) => {
                      if (link) {
                        window.location.replace(
                          `/${app.org_id}/boards/${link.id}`
                        );
                      }
                    });
                },
              },
              {
                key: "delete",
                icon: <DeleteOutlined />,
                label: <span>Delete</span>,
                danger: true,
                disabled: !board.userCanEdit || working,
                onClick() {
                  if (!board.link) return;
                  const proceed = confirm(
                    "Are you sure you want to delete this board?"
                  );
                  if (proceed) {
                    deep.remove.mutateAsync({ id: board.link.id }).then(() => {
                      router
                        .replace({
                          pathname: "/[orgId]/boards/start",
                          query: { orgId: router.query.orgId },
                        })
                        .then(() => {
                          toaster.addMessage(ToasterMessageType.SUCCESS, {
                            message: "Board deleted",
                          });
                        });
                    });
                  }
                },
              },
            ],
          }}
        >
          <Button
            id="primary-query-menu-button"
            style={{
              paddingLeft: 8,
              paddingRight: 8,
              justifyContent: "center",
              alignItems: "center",
            }}
            type="text"
            shape="round"
            // disabled={props.mutationInProcess || !props.userCanEdit}
          >
            {working ? (
              <LoadingOutlined />
            ) : (
              <EllipsisOutlined
                style={{ color: token.colorTextSecondary, fontWeight: 600 }}
              />
            )}
          </Button>
        </Dropdown>
      )}
    </>
  );
}

class PureQuestionInput extends PureComponent<{
  serialized: string;
  onChange(searchState: SearchState): void;
  onReset(): void;
}> {
  lastRun: { when: number; serial: string };
  constructor(props: {
    serialized: string;
    onChange(searchState: SearchState): void;
    onReset(): void;
  }) {
    /* console.log("constructing"); */
    super(props);
    this.lastRun = {
      when: Date.now(),
      serial: props.serialized,
    };
  }
  render() {
    /* console.log(
     *   "render: PURE_QUESTION_INPUT",
     *   ((Date.now() - this.lastRun.when) / 1000).toFixed(2),
     *   this.props.serialized
     * ); */
    this.lastRun = { when: Date.now(), serial: this.props.serialized };
    const value = JSON.parse(this.props.serialized) as SearchState;
    return (
      <QuestionInput
        collapsed
        floatAway
        alternateStyle
        expandOnFocus
        seed={this.props.serialized}
        value={value}
        resetTitle="Reset to defaults"
        resetIcon={<UndoOutlined />}
        hideDisabled
        onChange={this.props.onChange}
        handleReset={this.props.onReset}
        emptyText="Filter Overrides"
        options={{
          disableGroupBys: true,
          disableMeasures: true,
          disableSelect: true,
          disableSearchTerm: false,
          disableCompareWith: true,
          disableFormula: true,
          disableHaving: true,
          disableSnippets: false,
          disableSuggestions: false,
          addFromTeamSnippet: false,
        }}
        snippetTransformer={(state) =>
          ({
            duration: state.duration,
            startDate: state.startDate,
            endDate: state.endDate,
            filters: state.filters,
          } as SearchState)
        }
        snippetOptionFilter={(snippet) => {
          const json = snippet?.json as SnippetV1State | undefined;
          return (json?.params?.filters?.length || 0) > 0;
        }}
      />
    );
  }
}
